// Atlas layout + per-block-state render table (ENG-12/13). PURE (ENG-1): this
// defines WHICH tile each block face samples and the UV math; the client paints
// the actual pixels for these tile ids (src/client/render/textures/atlas.ts).
// The mesher consumes the render table so it never needs the registry per-vertex.
import type { Registries } from '../registry/load';
import { SHAPE, shapeIdFromName } from '../shapes';

export const TILE_PX = 16;
export const ATLAS_TILES = 512; // atlas width in tiles (power of two), one row tall

/** Tile ids painted by the client atlas. */
export const TILE = {
  GRASS_TOP: 0,
  GRASS_SIDE: 1,
  DIRT: 2,
  STONE: 3,
  WOOD_SIDE: 4,
  WOOD_END: 5,
  LEAVES: 6,
  SAND: 7,
  SANDSTONE: 8,
  GRAVEL: 9,
  WATER: 10,
  SNOW: 11,
  BEDROCK: 12,
  DEEPSLATE: 13,
  PLANK: 14,
  GRASS_TALL: 15,
  FLOWER: 16,
  TORCH: 17,
} as const;

export const LAYER_OPAQUE = 0;
export const LAYER_CUTOUT = 1;
export const LAYER_TRANSLUCENT = 2;

// M2.3 ore + stone-variant tiles, assigned programmatically (18..). The client
// atlas paints these from the same map (base + speck color).
export const ORE_NAMES = ['coal', 'iron', 'copper', 'gold', 'redstone', 'lapis', 'diamond', 'emerald'] as const;
export const STONE_VARIANT_NAMES = ['andesite', 'diorite', 'granite'] as const;
export const EXTRA_TILES: Record<string, number> = (() => {
  const m: Record<string, number> = {};
  let t = 18;
  for (const o of ORE_NAMES) {
    m[`${o}_ore`] = t++;
    m[`deepslate_${o}_ore`] = t++;
  }
  for (const s of STONE_VARIANT_NAMES) m[s] = t++;
  return m;
})();

// M2.4 tree + vegetation tiles, packed right after the ore/variant block.
const VEG_BASE = Math.max(...Object.values(EXTRA_TILES)) + 1;
export const VEG = {
  BIRCH_SIDE: VEG_BASE,
  BIRCH_END: VEG_BASE + 1,
  BIRCH_LEAVES: VEG_BASE + 2,
  SPRUCE_SIDE: VEG_BASE + 3,
  SPRUCE_END: VEG_BASE + 4,
  SPRUCE_LEAVES: VEG_BASE + 5,
  CACTUS_SIDE: VEG_BASE + 6,
  CACTUS_TOP: VEG_BASE + 7,
  DEAD_BUSH: VEG_BASE + 8,
  SUGAR_CANE: VEG_BASE + 9,
  POPPY: VEG_BASE + 10,
} as const;

// M4.2 crafted blocks (cobblestone + workstations), packed after vegetation.
const M4_BASE = VEG.POPPY + 1;
export const M4T = {
  COBBLESTONE: M4_BASE,
  CRAFTING_TOP: M4_BASE + 1,
  CRAFTING_SIDE: M4_BASE + 2,
  FURNACE_FRONT: M4_BASE + 3,
  FURNACE_SIDE: M4_BASE + 4,
  FURNACE_TOP: M4_BASE + 5,
  CHEST: M4_BASE + 6,
} as const;

// M7 nether blocks, packed after the M4 crafted set.
const NETHER_BASE = M4T.CHEST + 1;
export const NETHER = {
  NETHERRACK: NETHER_BASE,
  GLOWSTONE: NETHER_BASE + 1,
  SOUL_SAND: NETHER_BASE + 2,
  MAGMA: NETHER_BASE + 3,
  OBSIDIAN: NETHER_BASE + 4,
  PORTAL: NETHER_BASE + 5,
  QUARTZ_ORE: NETHER_BASE + 6,
  LAVA: NETHER_BASE + 7,
} as const;

// M8 Atlas blocks (city voxelization), packed after the nether set.
const ATLAS_BASE = NETHER.LAVA + 1;
export const ATLAS_BLK = {
  GLASS: ATLAS_BASE,
  BRICKS: ATLAS_BASE + 1,
  STONE_BRICKS: ATLAS_BASE + 2,
  GRAY_CONCRETE: ATLAS_BASE + 3,
  LADDER: ATLAS_BASE + 4,
} as const;

// M9 blocks (weather/beds), packed after the atlas set.
const M9_BASE = ATLAS_BLK.LADDER + 1;
export const M9T = {
  WHITE_WOOL: M9_BASE,
  BED_TOP: M9_BASE + 1,
  BED_SIDE: M9_BASE + 2,
} as const;

// M10 coloured families (BLK-scale, D20). 16 dye colours × 5 families, each a
// flat/tinted cube tile computed from (family, colour) so no per-block cases.
export const DYE_ORDER = [
  'white', 'orange', 'magenta', 'light_blue', 'yellow', 'lime', 'pink', 'gray',
  'light_gray', 'cyan', 'purple', 'blue', 'brown', 'green', 'red', 'black',
] as const;
export const FAMILY_ORDER = ['wool', 'concrete', 'concrete_powder', 'terracotta', 'stained_glass'] as const;
export const FAMILY_TILE_BASE = M9T.BED_SIDE + 1;

// M11 farming tiles, packed after the colour-family block (16 × 5 tiles).
const M11_BASE = M9T.BED_SIDE + 1 + 16 * 5;
export const M11T = {
  FARMLAND_DRY: M11_BASE, FARMLAND_WET: M11_BASE + 1,
  WHEAT_YOUNG: M11_BASE + 2, WHEAT_MATURE: M11_BASE + 3,
  CARROTS_YOUNG: M11_BASE + 4, CARROTS_MATURE: M11_BASE + 5,
  POTATOES_YOUNG: M11_BASE + 6, POTATOES_MATURE: M11_BASE + 7,
  BEETROOTS_YOUNG: M11_BASE + 8, BEETROOTS_MATURE: M11_BASE + 9,
  COMPOSTER: M11_BASE + 10,
  ENCHANT_TOP: M11_BASE + 11, BOOKSHELF: M11_BASE + 12,
} as const;

// M13 mob-roster blocks (iron golem / snow golem construction — M13.1).
const M13_BASE = M11T.BOOKSHELF + 1;
export const M13T = {
  IRON_BLOCK: M13_BASE,
  PUMPKIN_TOP: M13_BASE + 1,
  PUMPKIN_SIDE: M13_BASE + 2,
  CARVED_PUMPKIN_FRONT: M13_BASE + 3,
} as const;

// M19 amethyst-geode blocks (Caves & Cliffs v2). The four bud stages share one
// crystal billboard tile (per-stage geometry deferred).
const M19_BASE = M13T.CARVED_PUMPKIN_FRONT + 1;
export const M19T = {
  AMETHYST_BLOCK: M19_BASE,
  BUDDING_AMETHYST: M19_BASE + 1,
  CALCITE: M19_BASE + 2,
  SMOOTH_BASALT: M19_BASE + 3,
  AMETHYST_CRYSTAL: M19_BASE + 4,
} as const;

// M22 Deep Dark blocks (sculk family). The catalyst is a sculk body with a
// bloomed, veined top; plain sculk is the dark spreading block.
const M22_BASE = M19T.AMETHYST_CRYSTAL + 1;
export const M22T = {
  SCULK: M22_BASE,
  SCULK_CATALYST_TOP: M22_BASE + 1,
  SCULK_CATALYST_SIDE: M22_BASE + 2,
} as const;

// MB.1 — the remaining overworld wood species (oak is tiled by the base group).
// Nine species with distinct plank tiles total (oak + these eight). Spruce/birch
// already have log/leaf tiles in the VEG block (M2.4); they only need a plank +
// sapling here, so their log/leaf entries below reuse the existing VEG tiles.
export const WOOD_SPECIES = ['spruce', 'birch', 'jungle', 'acacia', 'dark_oak', 'mangrove', 'cherry', 'pale_oak'] as const;
export type WoodSpecies = (typeof WOOD_SPECIES)[number];
export interface WoodTiles { plank: number; logSide: number; logEnd: number; leaves: number; sapling: number; }
export const WOOD_TILES: Record<WoodSpecies, WoodTiles> = (() => {
  let t = M22T.SCULK_CATALYST_SIDE + 1;
  const next = (): number => t++;
  const out = {} as Record<WoodSpecies, WoodTiles>;
  for (const sp of WOOD_SPECIES) {
    const plank = next();
    const sapling = next();
    if (sp === 'spruce') out[sp] = { plank, sapling, logSide: VEG.SPRUCE_SIDE, logEnd: VEG.SPRUCE_END, leaves: VEG.SPRUCE_LEAVES };
    else if (sp === 'birch') out[sp] = { plank, sapling, logSide: VEG.BIRCH_SIDE, logEnd: VEG.BIRCH_END, leaves: VEG.BIRCH_LEAVES };
    else out[sp] = { plank, sapling, logSide: next(), logEnd: next(), leaves: next() };
  }
  return out;
})();
/** Highest wood tile id in use (atlas painter iterates up to here). */
export const WOOD_TILE_MAX = Math.max(...Object.values(WOOD_TILES).flatMap((w) => [w.plank, w.logSide, w.logEnd, w.leaves, w.sapling]));

// MB.2 — farming-breadth tiles (melon, bamboo, nether wart), packed after wood.
const MB2_BASE = WOOD_TILE_MAX + 1;
export const MB2T = {
  MELON_TOP: MB2_BASE,
  MELON_SIDE: MB2_BASE + 1,
  BAMBOO: MB2_BASE + 2,
  NETHER_WART: MB2_BASE + 3,
} as const;

// MB.1 pass 5 — stone-variant tiles (mossy / cracked / chiseled / smooth + the
// deepslate family), packed after the farming tiles.
const MB5_BASE = MB2T.NETHER_WART + 1;
export const MB5T = {
  MOSSY_COBBLESTONE: MB5_BASE,
  MOSSY_STONE_BRICKS: MB5_BASE + 1,
  CRACKED_STONE_BRICKS: MB5_BASE + 2,
  CHISELED_STONE_BRICKS: MB5_BASE + 3,
  SMOOTH_STONE: MB5_BASE + 4,
  COBBLED_DEEPSLATE: MB5_BASE + 5,
  POLISHED_DEEPSLATE: MB5_BASE + 6,
  DEEPSLATE_BRICKS: MB5_BASE + 7,
  DEEPSLATE_TILES: MB5_BASE + 8,
} as const;
/** stone-variant block → its base tile (also the DERIVED_BASE_TILE for its shapes). */
export const STONE_VARIANT_TILES: Record<string, number> = {
  mossy_cobblestone: MB5T.MOSSY_COBBLESTONE, mossy_stone_bricks: MB5T.MOSSY_STONE_BRICKS,
  cracked_stone_bricks: MB5T.CRACKED_STONE_BRICKS, chiseled_stone_bricks: MB5T.CHISELED_STONE_BRICKS,
  smooth_stone: MB5T.SMOOTH_STONE, cobbled_deepslate: MB5T.COBBLED_DEEPSLATE,
  polished_deepslate: MB5T.POLISHED_DEEPSLATE, deepslate_bricks: MB5T.DEEPSLATE_BRICKS,
  deepslate_tiles: MB5T.DEEPSLATE_TILES,
};

// MB.1 pass 6 — copper oxidation matrix. Four oxidation tiles per form
// (unaffected → oxidized); waxed variants reuse the same tile (they look
// identical, they just never weather).
const MB6_BASE = MB5T.DEEPSLATE_TILES + 1;
export const MB6T = {
  COPPER_BLOCK: MB6_BASE, EXPOSED_COPPER: MB6_BASE + 1, WEATHERED_COPPER: MB6_BASE + 2, OXIDIZED_COPPER: MB6_BASE + 3,
  CUT_COPPER: MB6_BASE + 4, EXPOSED_CUT_COPPER: MB6_BASE + 5, WEATHERED_CUT_COPPER: MB6_BASE + 6, OXIDIZED_CUT_COPPER: MB6_BASE + 7,
} as const;
/** copper block name (waxed or not) → its atlas tile. */
export const COPPER_TILES: Record<string, number> = (() => {
  const solid: [number, number, number, number] = [MB6T.COPPER_BLOCK, MB6T.EXPOSED_COPPER, MB6T.WEATHERED_COPPER, MB6T.OXIDIZED_COPPER];
  const cut: [number, number, number, number] = [MB6T.CUT_COPPER, MB6T.EXPOSED_CUT_COPPER, MB6T.WEATHERED_CUT_COPPER, MB6T.OXIDIZED_CUT_COPPER];
  const forms: [string, [number, number, number, number]][] = [
    ['copper_block', solid],
    ['cut_copper', cut],
    // The MB shape-forms reuse the oxidation-coloured copper tiles (chiseled → cut,
    // grate/bulb/openables → block/cut); distinct door/trapdoor art is a remainder.
    ['chiseled_copper', cut],
    ['copper_grate', solid],
    ['copper_bulb', solid],
    ['copper_door', cut],
    ['copper_trapdoor', cut],
  ];
  const ladders: Record<string, [string, string, string, string]> = {
    copper_block: ['copper_block', 'exposed_copper', 'weathered_copper', 'oxidized_copper'],
    cut_copper: ['cut_copper', 'exposed_cut_copper', 'weathered_cut_copper', 'oxidized_cut_copper'],
    chiseled_copper: ['chiseled_copper', 'exposed_chiseled_copper', 'weathered_chiseled_copper', 'oxidized_chiseled_copper'],
    copper_grate: ['copper_grate', 'exposed_copper_grate', 'weathered_copper_grate', 'oxidized_copper_grate'],
    copper_bulb: ['copper_bulb', 'exposed_copper_bulb', 'weathered_copper_bulb', 'oxidized_copper_bulb'],
    copper_door: ['copper_door', 'exposed_copper_door', 'weathered_copper_door', 'oxidized_copper_door'],
    copper_trapdoor: ['copper_trapdoor', 'exposed_copper_trapdoor', 'weathered_copper_trapdoor', 'oxidized_copper_trapdoor'],
  };
  const out: Record<string, number> = {};
  for (const [form, tiles] of forms) {
    for (let s = 0; s < 4; s++) {
      const n = ladders[form]![s]!;
      out[n] = tiles[s]!;
      out[`waxed_${n}`] = tiles[s]!;
    }
  }
  return out;
})();

// MB.3 pass 7 — campfire (rendered as a simplified lit cube: embers on top, log
// sides, ash base; the crossed-log model + lit/soul variants are remainders).
const MB7_BASE = MB6T.OXIDIZED_CUT_COPPER + 1;
export const MB7T = { CAMPFIRE_TOP: MB7_BASE, CAMPFIRE_SIDE: MB7_BASE + 1, CAMPFIRE_BASE: MB7_BASE + 2 } as const;

// MB.1 pass 8 — cauldron (iron staves + empty rim; a filled cauldron shows the
// shared water tile on top). Level render is a top-face swap, not a partial box.
const MB8_BASE = MB7T.CAMPFIRE_BASE + 1;
export const MB8T = { CAULDRON_SIDE: MB8_BASE, CAULDRON_TOP: MB8_BASE + 1 } as const;

// MB.1 — polished andesite / diorite / granite (smoother variants of the base
// stone tiles). Their stairs/slabs derive from these via STONE_VARIANT_TILES.
const MB9_BASE = MB8T.CAULDRON_TOP + 1;
export const MB9T = {
  POLISHED_ANDESITE: MB9_BASE, POLISHED_DIORITE: MB9_BASE + 1, POLISHED_GRANITE: MB9_BASE + 2,
} as const;
/** Polished-stone block name → tile (also feeds DERIVED_BASE_TILE for their shapes). */
export const POLISHED_STONE_TILES: Record<string, number> = {
  polished_andesite: MB9T.POLISHED_ANDESITE, polished_diorite: MB9T.POLISHED_DIORITE,
  polished_granite: MB9T.POLISHED_GRANITE,
};

// MB.1 — glazed terracotta ×16. One directional tile per dye colour (a diagonal
// glaze split so the per-facing top rotation is visible). Painted in the client.
const MBGLAZED_BASE = MB9T.POLISHED_GRANITE + 1;
/** Tile for a `<colour>_glazed_terracotta` block, or undefined if not one. */
export function glazedTerracottaTileOf(name: string): number | undefined {
  const suffix = '_glazed_terracotta';
  if (!name.endsWith(suffix)) return undefined;
  const ci = DYE_ORDER.indexOf(name.slice(0, -suffix.length) as (typeof DYE_ORDER)[number]);
  return ci >= 0 ? MBGLAZED_BASE + ci : undefined;
}
/** First/last glazed tile ids (the client paints this inclusive range). */
export const GLAZED_TILE_RANGE = [MBGLAZED_BASE, MBGLAZED_BASE + 15] as const;
/** Quarter-turns the glazed top texture rotates for a horizontal facing (the
 *  "directional render": neighbours placed different ways tile into a pattern). */
export function glazedTopRotation(facing: string | undefined): number {
  switch (facing) {
    case 'east': return 1;
    case 'south': return 2;
    case 'west': return 3;
    default: return 0; // north
  }
}

/** MB.1 clay chain: clay + base (undyed) terracotta tiles — the terracotta economy
 *  the 16 `<colour>_terracotta` recipes build from. Appended after the glazed group
 *  so existing tile ids are unchanged. */
const MB10_BASE = MBGLAZED_BASE + 16;
export const MB10T = { CLAY: MB10_BASE, TERRACOTTA: MB10_BASE + 1 } as const;

/** MB.1 / E-14: the translucent sticky blocks (slime green, honey amber) that
 *  carry the piston-adhesion + bounce/drag physics. Appended after the clay group
 *  so existing tile ids are unchanged. */
const MB11_BASE = MB10_BASE + 2;
export const MB11T = { SLIME: MB11_BASE, HONEY: MB11_BASE + 1 } as const;

/** M34 Texture Forge (FRG-2): one reserved tile the `forged_block` cosmetic
 *  variant renders from. Painted at load with the default forge params and
 *  re-skinned at runtime by the client painter (client/render/textures/forge.ts);
 *  its tile id never moves. Appended last so existing tile ids are unchanged. */
const FORGE_BASE = MB11T.HONEY + 1;
export const FORGE = { SKIN: FORGE_BASE } as const;

/** M35 Script Blocks (SCR-4): one reserved tile the creative/ops-only `script_block`
 *  renders from (a command-block-style face). Appended after the forge tile so every
 *  existing tile id is unchanged. */
const SCRIPT_BASE = FORGE.SKIN + 1;
export const SCRIPT_TILE = { BLOCK: SCRIPT_BASE } as const;

/** M20 Beacon (M20.1): the beacon's translucent quartz-glass body with a glowing
 *  star core. Appended after the script tile so every existing tile id is
 *  unchanged. */
const M20_BASE = SCRIPT_TILE.BLOCK + 1;
export const M20T = { BEACON: M20_BASE, WITHER_SKULL: M20_BASE + 1 } as const;

/** M18 Oceans (M18.1): the prismarine family + the conduit activator. Appended
 *  after the M20 tiles so every existing tile id is unchanged. */
const M18_BASE = M20T.WITHER_SKULL + 1;
export const M18T = {
  PRISMARINE: M18_BASE,
  PRISMARINE_BRICKS: M18_BASE + 1,
  DARK_PRISMARINE: M18_BASE + 2,
  SEA_LANTERN: M18_BASE + 3,
  CONDUIT: M18_BASE + 4,
} as const;

/** M22 sculk shrieker (M22.1): the warden-summoning deep-dark block. Appended after
 *  the M18 tiles so every existing tile id is unchanged. */
const M22B_BASE = M18T.CONDUIT + 1;
export const M22B = { SCULK_SHRIEKER: M22B_BASE } as const;

/** M21 End (M21.1): the end crystal + dragon egg. Appended after the M22B tile so
 *  every existing tile id is unchanged. */
const M21_BASE = M22B.SCULK_SHRIEKER + 1;
export const M21T = { END_CRYSTAL: M21_BASE, DRAGON_EGG: M21_BASE + 1 } as const;

/** M22 Trial chambers (M22.1): the trial spawner + vault. Appended after the M21
 *  tiles so every existing tile id is unchanged. */
const M22C_BASE = M21T.DRAGON_EGG + 1;
export const M22C = { TRIAL_SPAWNER: M22C_BASE, VAULT: M22C_BASE + 1 } as const;

/** M19 dripstone (M19.1): the pointed dripstone spike + dripstone block. Appended
 *  after the M22C tiles so every existing tile id is unchanged. */
const M19B_BASE = M22C.VAULT + 1;
export const M19B = { POINTED_DRIPSTONE: M19B_BASE, DRIPSTONE_BLOCK: M19B_BASE + 1 } as const;

/** M20 respawn anchor (M20.1): the glowstone-charged nether spawn block. Appended
 *  after the M19B tiles so every existing tile id is unchanged. */
const M20B_BASE = M19B.DRIPSTONE_BLOCK + 1;
export const M20B = { RESPAWN_ANCHOR: M20B_BASE } as const;

/** M22 sculk sensors (M40 pass): the deep-dark vibration→redstone blocks. Appended
 *  after M20B so every existing tile id is unchanged. */
const M40T_BASE = M20B.RESPAWN_ANCHOR + 1;
export const M40T = { SCULK_SENSOR: M40T_BASE, CALIBRATED_SCULK_SENSOR: M40T_BASE + 1 } as const;

/** Archaeology (M41 pass): the two suspicious blocks. Appended after M40T so every
 *  existing tile id is unchanged. */
const M41T_BASE = M40T.CALIBRATED_SCULK_SENSOR + 1;
export const M41T = { SUSPICIOUS_SAND: M41T_BASE, SUSPICIOUS_GRAVEL: M41T_BASE + 1 } as const;

/** Decorated pot (M42 pass): the sherd-crafted store. Appended after M41T. */
const M42T_BASE = M41T.SUSPICIOUS_GRAVEL + 1;
export const M42T = { DECORATED_POT: M42T_BASE } as const;

/** Torchflower crop (M44 pass): the sniffer's seed grown on farmland. Appended after M42T. */
const M44T_BASE = M42T.DECORATED_POT + 1;
export const M44T = { TORCHFLOWER_YOUNG: M44T_BASE, TORCHFLOWER_MATURE: M44T_BASE + 1 } as const;

/** Pitcher crop (M45 pass): the sniffer's other seed. Appended after M44T. */
const M45T_BASE = M44T.TORCHFLOWER_MATURE + 1;
export const M45T = { PITCHER_YOUNG: M45T_BASE, PITCHER_MATURE: M45T_BASE + 1 } as const;

/** Pink petals (M45 pass): cherry-grove ground cover. Appended after M45T. */
const M46T_BASE = M45T.PITCHER_MATURE + 1;
export const M46T = { PINK_PETALS: M46T_BASE } as const;

/** Nether biome ground (crimson/warped forests, soul sand valley, basalt deltas).
 *  Appended after M46T. Nylium is netherrack capped with fungal growth, so it needs
 *  a distinct top and side tile each. */
const M47T_BASE = M46T.PINK_PETALS + 1;
export const M47T = {
  CRIMSON_NYLIUM_TOP: M47T_BASE,
  CRIMSON_NYLIUM_SIDE: M47T_BASE + 1,
  WARPED_NYLIUM_TOP: M47T_BASE + 2,
  WARPED_NYLIUM_SIDE: M47T_BASE + 3,
  SOUL_SOIL: M47T_BASE + 4,
  BASALT_SIDE: M47T_BASE + 5,
  BASALT_TOP: M47T_BASE + 6,
  BLACKSTONE: M47T_BASE + 7,
} as const;

/** Nether flora (crimson/warped fungal forests). Appended after M47T. */
const M48T_BASE = M47T.BLACKSTONE + 1;
export const M48T = {
  CRIMSON_STEM_SIDE: M48T_BASE,
  CRIMSON_STEM_END: M48T_BASE + 1,
  WARPED_STEM_SIDE: M48T_BASE + 2,
  WARPED_STEM_END: M48T_BASE + 3,
  NETHER_WART_BLOCK: M48T_BASE + 4,
  WARPED_WART_BLOCK: M48T_BASE + 5,
  SHROOMLIGHT: M48T_BASE + 6,
  CRIMSON_FUNGUS: M48T_BASE + 7,
  WARPED_FUNGUS: M48T_BASE + 8,
  CRIMSON_ROOTS: M48T_BASE + 9,
  WARPED_ROOTS: M48T_BASE + 10,
} as const;

/** Nether structure masonry (fortress + bastion). Appended after M48T. */
const M49T_BASE = M48T.WARPED_ROOTS + 1;
export const M49T = {
  NETHER_BRICKS: M49T_BASE,
  POLISHED_BLACKSTONE: M49T_BASE + 1,
  GILDED_BLACKSTONE: M49T_BASE + 2,
} as const;

/** The End (DIM-3): end stone and the end-portal frame/void. Appended after M49T. */
const M50T_BASE = M49T.GILDED_BLACKSTONE + 1;
export const M50T = {
  END_STONE: M50T_BASE,
  END_PORTAL: M50T_BASE + 1,
  END_PORTAL_FRAME_TOP: M50T_BASE + 2,
  END_PORTAL_FRAME_SIDE: M50T_BASE + 3,
  END_PORTAL_FRAME_EYE: M50T_BASE + 4,
} as const;

/** End city + chorus flora (DIM-3). Appended after M50T. */
const M51T_BASE = M50T.END_PORTAL_FRAME_EYE + 1;
export const M51T = {
  PURPUR: M51T_BASE,
  PURPUR_PILLAR_SIDE: M51T_BASE + 1,
  PURPUR_PILLAR_END: M51T_BASE + 2,
  END_STONE_BRICKS: M51T_BASE + 3,
  END_ROD: M51T_BASE + 4,
  CHORUS_PLANT: M51T_BASE + 5,
  CHORUS_FLOWER: M51T_BASE + 6,
} as const;

/** Lush caves (GEN-6 aquifer/lush pass). Appended after M51T. */
const M52T_BASE = M51T.CHORUS_FLOWER + 1;
export const M52T = {
  MOSS: M52T_BASE,
  GLOW_LICHEN: M52T_BASE + 1,
  CAVE_VINES: M52T_BASE + 2,
  CAVE_VINES_BERRIES: M52T_BASE + 3,
} as const;

/** Per-amount pink-petal faces (M46 shipped one tile for all four amounts, so a
 *  single blossom looked identical to a full carpet). Appended after M52T. */
const M53T_BASE = M52T.CAVE_VINES_BERRIES + 1;
export const M53T = {
  /** Four densities, amount 1..4 → PETALS_1 .. PETALS_1 + 3. */
  PETALS_1: M53T_BASE,
} as const;

/** End gateway (DIM-3). Appended after M53T's four petal tiles. */
const M54T_BASE = M53T.PETALS_1 + 4;
export const M54T = {
  END_GATEWAY: M54T_BASE,
} as const;

/** Nether vines + sprouts (GEN-10 extension). Appended after M54T. */
const M55T_BASE = M54T.END_GATEWAY + 1;
export const M55T = {
  WEEPING_VINES: M55T_BASE,
  TWISTING_VINES: M55T_BASE + 1,
  NETHER_SPROUTS: M55T_BASE + 2,
} as const;

/** Lush-cave depth: rooted dirt, roots, blossoms, dripleaf, azalea (GEN-6
 *  extension). Appended after M55T. */
const M56T_BASE = M55T.NETHER_SPROUTS + 1;
export const M56T = {
  ROOTED_DIRT: M56T_BASE,
  HANGING_ROOTS: M56T_BASE + 1,
  SPORE_BLOSSOM: M56T_BASE + 2,
  SMALL_DRIPLEAF: M56T_BASE + 3,
  BIG_DRIPLEAF: M56T_BASE + 4,
  AZALEA: M56T_BASE + 5,
  FLOWERING_AZALEA: M56T_BASE + 6,
} as const;

/** Per-sherd decorated-pot faces + the wider flower family. Appended after M56T.
 *  The four sherds are in the pot's own recipe order (angler, archer, arms-up,
 *  skull), which is the order its four sides wear them. */
const M57T_BASE = M56T.FLOWERING_AZALEA + 1;
export const M57T = {
  POT_ANGLER: M57T_BASE,
  POT_ARCHER: M57T_BASE + 1,
  POT_ARMS_UP: M57T_BASE + 2,
  POT_SKULL: M57T_BASE + 3,
  POT_TOP: M57T_BASE + 4,
} as const;

/** The flower family beyond the M2 dandelion and poppy. Appended after M57T. */
export const FLOWERS = [
  'allium', 'azure_bluet', 'blue_orchid', 'cornflower', 'lily_of_the_valley',
  'oxeye_daisy', 'orange_tulip', 'pink_tulip', 'red_tulip', 'white_tulip',
] as const;
export type FlowerName = (typeof FLOWERS)[number];
const M58T_BASE = M57T.POT_TOP + 1;
/** `flowerTileOf(name)` → its tile; the family is laid out in FLOWERS order. */
export function flowerTileOf(name: string): number | undefined {
  const i = (FLOWERS as readonly string[]).indexOf(name);
  return i < 0 ? undefined : M58T_BASE + i;
}
export const M58T = { FIRST: M58T_BASE, LAST: M58T_BASE + FLOWERS.length - 1 } as const;

/** Netherite's chain: the ore you can never see, the ingot block, nether gold,
 *  and the smithing table that joins them. Appended after M58T. */
const M59T_BASE = M58T.LAST + 1;
export const M59T = {
  ANCIENT_DEBRIS_SIDE: M59T_BASE,
  ANCIENT_DEBRIS_TOP: M59T_BASE + 1,
  NETHERITE_BLOCK: M59T_BASE + 2,
  NETHER_GOLD_ORE: M59T_BASE + 3,
  SMITHING_TOP: M59T_BASE + 4,
  SMITHING_SIDE: M59T_BASE + 5,
  SMITHING_FRONT: M59T_BASE + 6,
} as const;

/** The stronghold's iron bars (DIM-3). Appended after M59T. */
const M60T_BASE = M59T.SMITHING_FRONT + 1;
export const M60T = {
  IRON_BARS: M60T_BASE,
} as const;

// Cotton (fibre crop): the renewable, peaceful source of string.
const M61T_BASE = M60T.IRON_BARS + 1;
export const M61T = {
  COTTON_YOUNG: M61T_BASE,
  COTTON_MATURE: M61T_BASE + 1,
  HYDRO_TOP: M61T_BASE + 2,
  HYDRO_SIDE: M61T_BASE + 3,
} as const;

/** Young/mature crop tile pair, by block name (M11.1). */
const CROP_TILES: Record<string, [number, number]> = {
  wheat: [M11T.WHEAT_YOUNG, M11T.WHEAT_MATURE],
  carrots: [M11T.CARROTS_YOUNG, M11T.CARROTS_MATURE],
  potatoes: [M11T.POTATOES_YOUNG, M11T.POTATOES_MATURE],
  beetroots: [M11T.BEETROOTS_YOUNG, M11T.BEETROOTS_MATURE],
  torchflower_crop: [M44T.TORCHFLOWER_YOUNG, M44T.TORCHFLOWER_MATURE],
  pitcher_crop: [M45T.PITCHER_YOUNG, M45T.PITCHER_MATURE],
  cotton: [M61T.COTTON_YOUNG, M61T.COTTON_MATURE],
};

/** Base tiles for the M10.2 shape families (they reuse an existing block tile). */
const DERIVED_BASE_TILE: Record<string, number> = {
  oak: TILE.PLANK, cobblestone: M4T.COBBLESTONE, stone: TILE.STONE,
  stone_brick: ATLAS_BLK.STONE_BRICKS, glass: ATLAS_BLK.GLASS,
  // Wood species: their stairs/slab/fence/gate/door/trapdoor derive from planks.
  ...Object.fromEntries(WOOD_SPECIES.map((sp) => [sp, WOOD_TILES[sp].plank])),
  // Stone variants: their stairs/slab/wall derive from the base variant tile.
  ...STONE_VARIANT_TILES,
  ...POLISHED_STONE_TILES,
  // Cut-copper stairs/slab derive from the cut_copper tile (MB.1).
  cut_copper: MB6T.CUT_COPPER,
  // Singular shape prefixes (block `mossy_stone_bricks` → `mossy_stone_brick_stairs`).
  mossy_stone_brick: MB5T.MOSSY_STONE_BRICKS,
  deepslate_brick: MB5T.DEEPSLATE_BRICKS,
  deepslate_tile: MB5T.DEEPSLATE_TILES,
};

/** Tiles for a wood-species block (`spruce_planks`, `jungle_log`, …), or null. */
function woodTilesFor(name: string, props: Readonly<Record<string, string>>): [number, number, number, number, number, number] | null {
  for (const sp of WOOD_SPECIES) {
    if (name === `${sp}_planks`) return fill(WOOD_TILES[sp].plank);
    if (name === `${sp}_leaves`) return fill(WOOD_TILES[sp].leaves);
    if (name === `${sp}_sapling`) return fill(WOOD_TILES[sp].sapling);
    if (name === `${sp}_log`) return logTiles(WOOD_TILES[sp].logSide, WOOD_TILES[sp].logEnd, props.axis);
    if (name === `${sp}_wood`) return fill(WOOD_TILES[sp].logSide); // bark on all six faces
    if (name === `stripped_${sp}_log`) return logTiles(WOOD_TILES[sp].plank, WOOD_TILES[sp].logEnd, props.axis);
    if (name === `stripped_${sp}_wood`) return fill(WOOD_TILES[sp].plank);
  }
  return null;
}
/** Tile for a derived shape block (`cobblestone_wall`, `glass_pane`, …), else undefined. */
export function derivedTileOf(name: string): number | undefined {
  for (const suffix of ['_fence_gate', '_trapdoor', '_fence', '_stairs', '_slab', '_wall', '_pane']) {
    if (!name.endsWith(suffix)) continue;
    const t = DERIVED_BASE_TILE[name.slice(0, -suffix.length)];
    if (t !== undefined) return t;
  }
  return undefined;
}

/** Tile for a coloured carpet: a carpet IS wool, so it samples the wool tile of
 *  its own colour and needs no atlas group of its own. (`moss_carpet` is not a
 *  dye colour, so it falls through to its own case.) */
export function carpetTileOf(name: string): number | undefined {
  if (!name.endsWith('_carpet')) return undefined;
  return familyTileOf(`${name.slice(0, -'_carpet'.length)}_wool`);
}

/** Tile for a `<colour>_<family>` block, or undefined if the name isn't one. */
export function familyTileOf(name: string): number | undefined {
  for (let fi = 0; fi < FAMILY_ORDER.length; fi++) {
    const suffix = `_${FAMILY_ORDER[fi]!}`;
    if (!name.endsWith(suffix)) continue;
    const ci = DYE_ORDER.indexOf(name.slice(0, -suffix.length) as (typeof DYE_ORDER)[number]);
    if (ci >= 0) return FAMILY_TILE_BASE + fi * 16 + ci;
  }
  return undefined;
}

/** UV for a tile corner with the legacy 0.02-texel inset (avoids bleeding). */
export function faceUV(tile: number, u: number, v: number): [number, number] {
  return [(tile + 0.02 + u * 0.96) / ATLAS_TILES, 0.02 + v * 0.96];
}

// Face tile order is [-x, +x, -y, +y, -z, +z].
const FACE_INDEX: Record<string, number> = { west: 0, east: 1, down: 2, up: 3, north: 4, south: 5 };
const OPPOSITE_FACE: Record<string, string> = { west: 'east', east: 'west', down: 'up', up: 'down', north: 'south', south: 'north' };
function tilesFor(name: string, props: Readonly<Record<string, string>>): [number, number, number, number, number, number] {
  const T = TILE;
  const wood = woodTilesFor(name, props);
  if (wood) return wood;
  const stoneVar = STONE_VARIANT_TILES[name] ?? POLISHED_STONE_TILES[name];
  if (stoneVar !== undefined) return fill(stoneVar);
  const copper = COPPER_TILES[name];
  if (copper !== undefined) return fill(copper);
  const glazed = glazedTerracottaTileOf(name);
  if (glazed !== undefined) return fill(glazed);
  switch (name) {
    case 'beehive': return fill(T.PLANK); // woody hive (approximation, MB.5)
    case 'oak_sign': return fill(T.PLANK); // wooden sign board (text drawn by the client, MB.1)
    case 'campfire': // embers up, logs on the sides, ash below (MB.3)
    case 'soul_campfire': // soul campfire reuses the ember tiles (blue flame approx.)
      return [MB7T.CAMPFIRE_SIDE, MB7T.CAMPFIRE_SIDE, MB7T.CAMPFIRE_BASE, MB7T.CAMPFIRE_TOP, MB7T.CAMPFIRE_SIDE, MB7T.CAMPFIRE_SIDE];
    case 'cauldron': { // iron staves; a filled cauldron shows water on top (MB.1)
      const top = Number(props.level ?? '0') > 0 ? T.WATER : MB8T.CAULDRON_TOP;
      return [MB8T.CAULDRON_SIDE, MB8T.CAULDRON_SIDE, MB8T.CAULDRON_SIDE, top, MB8T.CAULDRON_SIDE, MB8T.CAULDRON_SIDE];
    }
    case 'grass_block':
      return [T.GRASS_SIDE, T.GRASS_SIDE, T.DIRT, T.GRASS_TOP, T.GRASS_SIDE, T.GRASS_SIDE];
    case 'dirt':
      return [T.DIRT, T.DIRT, T.DIRT, T.DIRT, T.DIRT, T.DIRT];
    case 'stone':
      return [T.STONE, T.STONE, T.STONE, T.STONE, T.STONE, T.STONE];
    case 'oak_leaves':
      return [T.LEAVES, T.LEAVES, T.LEAVES, T.LEAVES, T.LEAVES, T.LEAVES];
    case 'oak_log':
      return logTiles(T.WOOD_SIDE, T.WOOD_END, props.axis);
    case 'birch_log':
      return logTiles(VEG.BIRCH_SIDE, VEG.BIRCH_END, props.axis);
    case 'spruce_log':
      return logTiles(VEG.SPRUCE_SIDE, VEG.SPRUCE_END, props.axis);
    case 'birch_leaves': return fill(VEG.BIRCH_LEAVES);
    case 'spruce_leaves': return fill(VEG.SPRUCE_LEAVES);
    case 'cactus': {
      const s = VEG.CACTUS_SIDE, t = VEG.CACTUS_TOP;
      return [s, s, t, t, s, s];
    }
    case 'dead_bush': return fill(VEG.DEAD_BUSH);
    case 'sugar_cane': return fill(VEG.SUGAR_CANE);
    case 'bamboo': return fill(MB2T.BAMBOO);
    case 'nether_wart': return fill(MB2T.NETHER_WART);
    case 'anvil': return fill(M13T.IRON_BLOCK); // dark iron approximation (MB.4)
    case 'grindstone': return fill(TILE.STONE);
    case 'infested_stone': return fill(TILE.STONE); // MB.6 — look like their host block
    case 'infested_cobblestone': return fill(M4T.COBBLESTONE);
    case 'infested_stone_bricks': return fill(ATLAS_BLK.STONE_BRICKS);
    case 'infested_deepslate': return fill(TILE.DEEPSLATE);
    case 'melon_stem': case 'pumpkin_stem': return fill(VEG.SUGAR_CANE); // green cross stem
    case 'cocoa': return fill(TILE.DIRT); // brown pod (approximation)
    case 'sweet_berry_bush': return fill(VEG.SUGAR_CANE); // leafy bush (approximation)
    case 'scaffolding': return fill(MB2T.BAMBOO); // bamboo lattice (approximation)
    case 'cake': return fill(TILE.SNOW); // white icing (approximation)
    case 'melon': {
      const s = MB2T.MELON_SIDE, t = MB2T.MELON_TOP;
      return [s, s, t, t, s, s];
    }
    case 'poppy': return fill(VEG.POPPY);
    // The rest of the flower family — each its own bloom rather than a recoloured
    // poppy, since telling them apart at a glance is the entire point of a meadow.
    case 'allium': case 'azure_bluet': case 'blue_orchid': case 'cornflower':
    case 'lily_of_the_valley': case 'oxeye_daisy':
    case 'orange_tulip': case 'pink_tulip': case 'red_tulip': case 'white_tulip':
      return fill(flowerTileOf(name)!);
    case 'sand': return fill(T.SAND);
    case 'sandstone': return fill(T.SANDSTONE);
    case 'gravel': return fill(T.GRAVEL);
    case 'water': return fill(T.WATER);
    case 'snow_block': return fill(T.SNOW);
    case 'snow': return fill(T.SNOW);
    case 'powder_snow': return fill(T.SNOW); // fluffy white (approximation, MB.1)
    case 'bedrock': return fill(T.BEDROCK);
    case 'deepslate': return fill(T.DEEPSLATE);
    case 'oak_slab': return fill(T.PLANK);
    case 'oak_stairs': return fill(T.PLANK);
    case 'oak_planks': return fill(T.PLANK);
    case 'oak_door': return fill(T.PLANK);
    case 'cobblestone': return fill(M4T.COBBLESTONE);
    case 'crafting_table': {
      const s = M4T.CRAFTING_SIDE;
      return [s, s, T.PLANK, M4T.CRAFTING_TOP, s, s];
    }
    case 'note_block': return fill(M4T.CRAFTING_SIDE); // dark planks w/ speck (approximation, MB.1)
    case 'crafter': {
      const s = M4T.CRAFTING_SIDE, t = M4T.CRAFTING_TOP; // crafting-grid look (approximation, MB)
      return [s, s, t, t, s, s];
    }
    case 'furnace': case 'smoker': {
      const s = M4T.FURNACE_SIDE, t = M4T.FURNACE_TOP;
      return [s, s, t, t, M4T.FURNACE_FRONT, s]; // front on -z (smoker reuses furnace faces)
    }
    case 'chest': return fill(M4T.CHEST);
    case 'netherrack': return fill(NETHER.NETHERRACK);
    case 'glowstone': return fill(NETHER.GLOWSTONE);
    case 'soul_sand': return fill(NETHER.SOUL_SAND);
    case 'magma_block': return fill(NETHER.MAGMA);
    case 'ochre_froglight': case 'verdant_froglight': case 'pearlescent_froglight':
      return fill(NETHER.GLOWSTONE); // glowy froglight (colour approximation, MB.5)
    case 'obsidian': return fill(NETHER.OBSIDIAN);
    case 'nether_portal': return fill(NETHER.PORTAL);
    case 'nether_quartz_ore': return fill(NETHER.QUARTZ_ORE);
    case 'nether_gold_ore': return fill(M59T.NETHER_GOLD_ORE);
    case 'iron_bars': return fill(M60T.IRON_BARS);
    case 'netherite_block': return fill(M59T.NETHERITE_BLOCK);
    case 'ancient_debris': {
      const s = M59T.ANCIENT_DEBRIS_SIDE, t = M59T.ANCIENT_DEBRIS_TOP;
      return [s, s, t, t, s, s]; // the scarred top/bottom faces read differently
    }
    case 'smithing_table': {
      const s = M59T.SMITHING_SIDE, f = M59T.SMITHING_FRONT;
      return [s, s, s, M59T.SMITHING_TOP, f, s]; // tools laid out on the front
    }
    case 'lava': return fill(NETHER.LAVA);
    case 'glass': return fill(ATLAS_BLK.GLASS);
    case 'clay': return fill(MB10T.CLAY);
    case 'terracotta': return fill(MB10T.TERRACOTTA);
    case 'slime_block': return fill(MB11T.SLIME);
    case 'honey_block': return fill(MB11T.HONEY);
    case 'forged_block': return fill(FORGE.SKIN); // M34: re-skinned at runtime (FRG-2)
    case 'script_block': return fill(SCRIPT_TILE.BLOCK); // M35: creative/ops command-block successor (SCR-4)
    case 'beacon': return fill(M20T.BEACON); // M20: pyramid-powered effect projector
    case 'wither_skeleton_skull': return fill(M20T.WITHER_SKULL); // M20: wither summon ingredient
    case 'prismarine': return fill(M18T.PRISMARINE); // M18 ocean frame blocks
    case 'prismarine_bricks': return fill(M18T.PRISMARINE_BRICKS);
    case 'dark_prismarine': return fill(M18T.DARK_PRISMARINE);
    case 'sea_lantern': return fill(M18T.SEA_LANTERN);
    case 'conduit': return fill(M18T.CONDUIT);
    case 'sculk_shrieker': return fill(M22B.SCULK_SHRIEKER); // M22: warden-summoning block
    case 'end_crystal': return fill(M21T.END_CRYSTAL); // M21: heals the ender dragon
    case 'dragon_egg': return fill(M21T.DRAGON_EGG); // M21: the dragon's trophy drop
    case 'trial_spawner': return fill(M22C.TRIAL_SPAWNER); // M22: per-player wave spawner
    case 'vault': return fill(M22C.VAULT); // M22: key-unlocked reward block
    case 'pointed_dripstone': return fill(M19B.POINTED_DRIPSTONE); // M19: stalactite/stalagmite spike
    case 'dripstone_block': return fill(M19B.DRIPSTONE_BLOCK);
    case 'respawn_anchor': return fill(M20B.RESPAWN_ANCHOR); // M20: glowstone-charged nether spawn
    case 'sculk_sensor': return fill(M40T.SCULK_SENSOR); // M22: vibration→redstone sensor
    case 'calibrated_sculk_sensor': return fill(M40T.CALIBRATED_SCULK_SENSOR);
    case 'suspicious_sand': return fill(M41T.SUSPICIOUS_SAND); // archaeology: brushable finds
    case 'suspicious_gravel': return fill(M41T.SUSPICIOUS_GRAVEL);
    // A pot is crafted from FOUR sherds and wears one on each side, in recipe
    // order — the whole point of a decorated pot is that you can read what went
    // into it. Top and bottom are the plain rim. Face order is [-X,+X,-Y,+Y,-Z,+Z].
    case 'decorated_pot': return [
      M57T.POT_ARMS_UP, M57T.POT_ARCHER, M57T.POT_TOP, M57T.POT_TOP, M57T.POT_ANGLER, M57T.POT_SKULL,
    ];
    case 'pink_petals': {
      // The `flower_amount` (1..4) picks a denser face, so a lone blossom and a full
      // carpet no longer render identically. The M46 tile is amount 4's.
      const n = Math.min(4, Math.max(1, Number(props.flower_amount ?? '1')));
      return fill(n >= 4 ? M46T.PINK_PETALS : M53T.PETALS_1 + (n - 1));
    }
    // Nether biome ground. Nylium is fungal growth ON netherrack, so it caps like a
    // grass block: fungal top, fading side, bare netherrack underneath.
    case 'crimson_nylium': {
      const s = M47T.CRIMSON_NYLIUM_SIDE;
      return [s, s, NETHER.NETHERRACK, M47T.CRIMSON_NYLIUM_TOP, s, s];
    }
    case 'warped_nylium': {
      const s = M47T.WARPED_NYLIUM_SIDE;
      return [s, s, NETHER.NETHERRACK, M47T.WARPED_NYLIUM_TOP, s, s];
    }
    case 'soul_soil': return fill(M47T.SOUL_SOIL);
    case 'blackstone': return fill(M47T.BLACKSTONE);
    case 'basalt': return logTiles(M47T.BASALT_SIDE, M47T.BASALT_TOP, props.axis);
    // Nether flora: fungal "trees" and their ground cover.
    case 'crimson_stem': return logTiles(M48T.CRIMSON_STEM_SIDE, M48T.CRIMSON_STEM_END, props.axis);
    case 'warped_stem': return logTiles(M48T.WARPED_STEM_SIDE, M48T.WARPED_STEM_END, props.axis);
    case 'nether_wart_block': return fill(M48T.NETHER_WART_BLOCK);
    case 'warped_wart_block': return fill(M48T.WARPED_WART_BLOCK);
    case 'shroomlight': return fill(M48T.SHROOMLIGHT);
    case 'crimson_fungus': return fill(M48T.CRIMSON_FUNGUS);
    case 'warped_fungus': return fill(M48T.WARPED_FUNGUS);
    case 'crimson_roots': return fill(M48T.CRIMSON_ROOTS);
    case 'warped_roots': return fill(M48T.WARPED_ROOTS);
    // Nether masonry (fortress corridors + bastion walls).
    case 'nether_bricks': return fill(M49T.NETHER_BRICKS);
    case 'polished_blackstone': return fill(M49T.POLISHED_BLACKSTONE);
    case 'gilded_blackstone': return fill(M49T.GILDED_BLACKSTONE);
    // The End.
    case 'end_stone': return fill(M50T.END_STONE);
    case 'end_portal': return fill(M50T.END_PORTAL);
    case 'end_gateway': return fill(M54T.END_GATEWAY);
    case 'weeping_vines': return fill(M55T.WEEPING_VINES);
    case 'twisting_vines': return fill(M55T.TWISTING_VINES);
    case 'nether_sprouts': return fill(M55T.NETHER_SPROUTS);
    case 'rooted_dirt': return fill(M56T.ROOTED_DIRT);
    case 'hanging_roots': return fill(M56T.HANGING_ROOTS);
    case 'spore_blossom': return fill(M56T.SPORE_BLOSSOM);
    case 'small_dripleaf': return fill(M56T.SMALL_DRIPLEAF);
    case 'big_dripleaf': return fill(M56T.BIG_DRIPLEAF);
    case 'azalea': return fill(M56T.AZALEA);
    case 'flowering_azalea': return fill(M56T.FLOWERING_AZALEA);
    case 'purpur_block': return fill(M51T.PURPUR);
    case 'purpur_pillar': return logTiles(M51T.PURPUR_PILLAR_SIDE, M51T.PURPUR_PILLAR_END, props.axis);
    case 'end_stone_bricks': return fill(M51T.END_STONE_BRICKS);
    case 'end_rod': return fill(M51T.END_ROD);
    case 'chorus_plant': return fill(M51T.CHORUS_PLANT);
    case 'chorus_flower': return fill(M51T.CHORUS_FLOWER);
    // Lush caves.
    case 'moss_block': return fill(M52T.MOSS);
    case 'moss_carpet': return fill(M52T.MOSS);
    case 'glow_lichen': return fill(M52T.GLOW_LICHEN);
    case 'cave_vines': return fill(props.berries === '1' ? M52T.CAVE_VINES_BERRIES : M52T.CAVE_VINES);
    case 'end_portal_frame': {
      const s = M50T.END_PORTAL_FRAME_SIDE;
      const top = props.eye === 'true' ? M50T.END_PORTAL_FRAME_EYE : M50T.END_PORTAL_FRAME_TOP;
      return [s, s, s, top, s, s];
    }
    case 'bricks': return fill(ATLAS_BLK.BRICKS);
    case 'stone_bricks': return fill(ATLAS_BLK.STONE_BRICKS);
    case 'gray_concrete': return fill(ATLAS_BLK.GRAY_CONCRETE);
    case 'ladder': return fill(ATLAS_BLK.LADDER);
    case 'white_wool': return fill(M9T.WHITE_WOOL);
    case 'bed': {
      const t = M9T.BED_TOP, s = M9T.BED_SIDE;
      return [s, s, s, t, s, s]; // mattress on top, red sides
    }
    case 'tall_grass': return fill(T.GRASS_TALL);
    case 'dandelion': return fill(T.FLOWER);
    case 'torch': return fill(T.TORCH);
    case 'wall_torch': case 'redstone_wall_torch': return fill(T.TORCH); // wall-mount (angled geometry is a render remainder, MB.1)
    case 'farmland': {
      const top = Number(props.moisture ?? '0') > 0 ? M11T.FARMLAND_WET : M11T.FARMLAND_DRY;
      return [T.DIRT, T.DIRT, T.DIRT, top, T.DIRT, T.DIRT];
    }
    case 'composter': return fill(M11T.COMPOSTER);
    case 'enchanting_table': {
      const t = M11T.ENCHANT_TOP, s = NETHER.OBSIDIAN;
      return [s, s, s, t, s, s];
    }
    case 'bookshelf': {
      const s = M11T.BOOKSHELF, p = TILE.PLANK;
      return [s, s, p, p, s, s]; // book spines on the sides, planks top/bottom
    }
    case 'wheat': case 'carrots': case 'potatoes': case 'beetroots': case 'torchflower_crop': case 'pitcher_crop': case 'cotton': {
      const [young, mature] = CROP_TILES[name]!;
      const maxAge = name === 'beetroots' ? 3 : name === 'torchflower_crop' ? 2 : name === 'pitcher_crop' ? 4 : 7;
      return fill(Number(props.age ?? '0') >= maxAge ? mature : young);
    }
    case 'hydroponic_tray': {
      const top = M61T.HYDRO_TOP, side = M61T.HYDRO_SIDE;
      return [side, side, side, top, side, side]; // index 3 is +Y: the water surface
    }
    case 'iron_block': return fill(M13T.IRON_BLOCK);
    case 'bell': return fill(M13T.IRON_BLOCK); // gold-topped bell (iron approx., MB.6)
    case 'brewing_stand': return fill(M13T.IRON_BLOCK); // iron stand (approx., MB.4)
    case 'pumpkin': {
      const t = M13T.PUMPKIN_TOP, s = M13T.PUMPKIN_SIDE;
      return [s, s, t, t, s, s];
    }
    case 'carved_pumpkin': {
      // Carved face on the block's facing side; ridged pumpkin skin elsewhere.
      const t = M13T.PUMPKIN_TOP, s = M13T.PUMPKIN_SIDE, f = M13T.CARVED_PUMPKIN_FRONT;
      const faces: [number, number, number, number, number, number] = [s, s, t, t, s, s];
      const dir = props.facing ?? 'north';
      const idx = dir === 'east' ? 1 : dir === 'south' ? 5 : dir === 'west' ? 0 : 4; // -x,+x,-y,+y,-z,+z
      faces[idx] = f;
      return faces;
    }
    case 'piston': case 'sticky_piston': {
      // Directional cube (M16.1): planks on the pushing face, iron on the base
      // back, cobble body. Proper piston-arm geometry is deferred to the 'piston'
      // shape mesh (M16.1-remainder); the base renders as a cube for now.
      const body = M4T.COBBLESTONE, plate = T.PLANK, back = M13T.IRON_BLOCK;
      const faces: [number, number, number, number, number, number] = [body, body, body, body, body, body];
      const dir = props.facing ?? 'up';
      faces[FACE_INDEX[dir] ?? 3] = plate;
      faces[FACE_INDEX[OPPOSITE_FACE[dir] ?? 'down'] ?? 2] = back;
      return faces;
    }
    case 'piston_head': return fill(T.PLANK); // the wooden arm (approximate)
    case 'rail': case 'powered_rail': case 'detector_rail': return fill(ATLAS_BLK.LADDER); // flat 'layer'; lattice reads as ties (proper rail tiles deferred)
    case 'amethyst_block': return fill(M19T.AMETHYST_BLOCK);
    case 'budding_amethyst': return fill(M19T.BUDDING_AMETHYST);
    case 'calcite': return fill(M19T.CALCITE);
    case 'smooth_basalt': return fill(M19T.SMOOTH_BASALT);
    case 'small_amethyst_bud': case 'medium_amethyst_bud':
    case 'large_amethyst_bud': case 'amethyst_cluster':
      return fill(M19T.AMETHYST_CRYSTAL); // crystal billboard; facing-specific geometry deferred
    case 'sculk': return fill(M22T.SCULK);
    case 'sculk_catalyst': {
      const s = M22T.SCULK_CATALYST_SIDE, t = M22T.SCULK_CATALYST_TOP, b = M22T.SCULK;
      return [s, s, b, t, s, s]; // bloomed top, sculk sides, plain-sculk bottom
    }
    case 'observer': {
      // The "eye" (iron) on the watched face, cobble on the output back, deepslate body.
      const body = T.DEEPSLATE, eye = M13T.IRON_BLOCK, out = M4T.COBBLESTONE;
      const faces: [number, number, number, number, number, number] = [body, body, body, body, body, body];
      const dir = props.facing ?? 'south';
      faces[FACE_INDEX[dir] ?? 5] = eye;
      faces[FACE_INDEX[OPPOSITE_FACE[dir] ?? 'north'] ?? 4] = out;
      return faces;
    }
    default: {
      const derived = derivedTileOf(name);
      if (derived !== undefined) return fill(derived);
      // A tinted pane (`red_stained_glass_pane`) reuses its stained-glass tile, and
      // a coloured carpet (M61) reuses the wool tile of its own colour.
      const fam = familyTileOf(name)
        ?? carpetTileOf(name)
        ?? (name.endsWith('_pane') ? familyTileOf(name.slice(0, -5)) : undefined);
      if (fam !== undefined) return fill(fam);
      const ex = EXTRA_TILES[name];
      return ex !== undefined ? fill(ex) : fill(T.STONE);
    }
  }
}

function fill(t: number): [number, number, number, number, number, number] {
  return [t, t, t, t, t, t];
}

/** Log face tiles for the given axis: `end` caps the two axis-aligned faces. */
function logTiles(side: number, end: number, axis: string | undefined): [number, number, number, number, number, number] {
  if (axis === 'x') return [end, end, side, side, side, side];
  if (axis === 'z') return [side, side, side, side, end, end];
  return [side, side, end, end, side, side]; // y (upright)
}

export interface RenderTable {
  /** stateId -> 1 if the block should be meshed at all (non-air). */
  hasMesh: Uint8Array;
  /** stateId -> 1 if a full opaque cube (culls neighbors, casts AO). */
  opaque: Uint8Array;
  /** stateId -> LAYER_OPAQUE | LAYER_CUTOUT | LAYER_TRANSLUCENT. */
  layer: Uint8Array;
  /** stateId -> shape id (SHAPE.*). */
  shape: Uint8Array;
  /** stateId*6 + face -> tile id. */
  tiles: Int16Array;
  /** stateId -> quarter-turns to rotate the top/bottom face UV (glazed terracotta
   *  directional render); 0 for everything else. */
  rotTop: Uint8Array;
  /** stateId -> owning block index (for same-block cutout culling). */
  blockOf: Int32Array;
  /** stateId -> resolved property values (for shape geometry). */
  props: readonly Readonly<Record<string, string>>[];
  /** stateId -> 1 if the block is waterlogged (holds a source-water pass). */
  waterlogged: Uint8Array;
  /** Water source state id (level 0), or -1 if the registry omits water. */
  waterState: number;
  /** Block index of `water` (for fluid self-merge incl. waterlogged), or -1. */
  waterBlock: number;
}

/** Build the per-state render lookup once at load; the mesher indexes it hot. */
export function buildRenderTable(registries: Registries): RenderTable {
  const { count, blockName, props } = registries.states;
  const hasMesh = new Uint8Array(count);
  const opaque = new Uint8Array(count);
  const layer = new Uint8Array(count);
  const shape = new Uint8Array(count);
  const tiles = new Int16Array(count * 6);
  const rotTop = new Uint8Array(count);
  const blockOf = new Int32Array(count);

  const waterlogged = new Uint8Array(count);

  const blockIndex = new Map<string, number>();
  for (const b of registries.blocks.list) blockIndex.set(b.name, blockIndex.size);
  const waterState = registries.states.defaultOf.get('water') ?? -1;
  const waterBlock = blockIndex.get('water') ?? -1;

  for (let s = 0; s < count; s++) {
    const name = blockName[s]!;
    const def = registries.blocks.byName.get(name)!;
    blockOf[s] = blockIndex.get(name)!;
    shape[s] = shapeIdFromName(def.shape);
    if (def.material === 'air' || def.shape === 'empty') continue;
    hasMesh[s] = 1;
    // Waterlogged non-fluid blocks (slab/stairs/fence/…) hold source water that
    // the mesher draws as an extra fluid pass around the block's shape (M9.1).
    if (def.material !== 'water' && props[s]!.waterlogged === 'true') waterlogged[s] = 1;
    const isLeaves = def.material === 'leaves';
    const isWater = def.material === 'water';
    const isLava = def.material === 'lava';
    const isGlass = def.material === 'glass';
    const isPortal = shape[s] === SHAPE.PORTAL;
    const isPlant = shape[s] === SHAPE.CROSS || shape[s] === SHAPE.TORCH;
    // Only full opaque cubes occlude neighbors / cast AO.
    opaque[s] = shape[s] === SHAPE.CUBE && !isLeaves && !isWater && !isGlass ? 1 : 0;
    // Water, lava, glass, and the portal plane render in the translucent pass (self-merging).
    layer[s] = isWater || isLava || isGlass || isPortal ? LAYER_TRANSLUCENT : isLeaves || isPlant ? LAYER_CUTOUT : LAYER_OPAQUE;
    const t = tilesFor(name, props[s]!);
    for (let f = 0; f < 6; f++) tiles[s * 6 + f] = t[f]!;
    if (glazedTerracottaTileOf(name) !== undefined) rotTop[s] = glazedTopRotation(props[s]!.facing);
  }
  return { hasMesh, opaque, layer, shape, tiles, rotTop, blockOf, props, waterlogged, waterState, waterBlock };
}
